// src/pages/subscription/PaymentPendingPage.jsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  Clock, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  CreditCard,
  ArrowRight,
  Image as ImageIcon
} from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../hooks/useAuth';
import { getImageUrl } from '../../utils/imageUtils';
import { formatPrice } from '../../utils/subscriptionUtils';
import styles from './PaymentPendingPage.module.css';

const PaymentPendingPage = () => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const [showImage, setShowImage] = useState(false);

  // Try to get data from URL search params first, then fall back to navigation state
  const orderId = searchParams.get('order_id') || location.state?.orderId || '';
  const amount = searchParams.get('amount') || location.state?.amount || '0';
  const planName = searchParams.get('plan_name') || location.state?.planName || '';
  const proofUrl = searchParams.get('proof_url') || location.state?.proofUrl || '';
  const currency = searchParams.get('currency') || user?.business?.currency || 'LKR';

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleViewSubscription = () => {
    navigate('/subscription');
  };

  const handleViewProof = () => {
    setShowImage(true);
  };

  const handleCloseImage = () => {
    setShowImage(false);
  };

  const approvalSteps = [
    {
      icon: FileText,
      title: t('subscription.payment.step1Title'),
      description: t('subscription.payment.step1Description'),
      status: 'completed'
    },
    {
      icon: Clock,
      title: t('subscription.payment.step2Title'),
      description: t('subscription.payment.step2Description'),
      status: 'current'
    },
    {
      icon: CheckCircle,
      title: t('subscription.payment.step3Title'),
      description: t('subscription.payment.step3Description'),
      status: 'pending'
    }
  ];

  return (
    <div className={styles.paymentPendingPage}>
      <div className={styles.pendingCard}>
        <div className={styles.iconWrapper}>
          <div className={styles.pendingIcon}>
            <Clock size={64} />
          </div>
        </div>

        <div className={styles.pendingHeader}>
          <h1>{t('subscription.payment.pendingTitle')}</h1>
          <p>{t('subscription.payment.pendingMessage')}</p>
        </div>

        {orderId && (
          <div className={styles.orderInfo}>
            <div className={styles.orderLabel}>{t('subscription.payment.orderId')}</div>
            <div className={styles.orderValue}>{orderId}</div>
          </div>
        )}

        <div className={styles.paymentDetails}>
          <h2>{t('subscription.payment.paymentDetails')}</h2>
          
          <div className={styles.detailsGrid}>
            {planName && (
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <FileText size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>{t('subscription.planName')}</div>
                  <div className={styles.detailValue}>{planName}</div>
                </div>
              </div>
            )}

            {amount && (
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <CreditCard size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>{t('subscription.amount')}</div>
                  <div className={styles.detailValue}>
                    {formatPrice(parseFloat(amount), currency)}
                  </div>
                </div>
              </div>
            )}

            {proofUrl && (
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <ImageIcon size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>{t('subscription.payment.paymentProof')}</div>
                  <button 
                    className={styles.viewProofButton}
                    onClick={handleViewProof}
                  >
                    {t('subscription.payment.viewProof')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.timeline}>
          <h2>{t('subscription.payment.approvalProcess')}</h2>
          <div className={styles.timelineSteps}>
            {approvalSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className={`${styles.timelineStep} ${styles[step.status]}`}>
                  <div className={styles.stepIcon}>
                    <Icon size={24} />
                  </div>
                  <div className={styles.stepContent}>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                  {index < approvalSteps.length - 1 && (
                    <div className={styles.stepConnector} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.estimatedTime}>
          <AlertCircle size={20} />
          <div className={styles.estimatedTimeContent}>
            <div className={styles.estimatedTimeTitle}>
              {t('subscription.payment.estimatedApprovalTime')}
            </div>
            <div className={styles.estimatedTimeValue}>
              {t('subscription.payment.approvalTimeRange')}
            </div>
          </div>
        </div>

        <div className={styles.infoBox}>
          <h3>{t('subscription.payment.whatHappensNext')}</h3>
          <ul>
            <li>
              <CheckCircle size={16} />
              <span>{t('subscription.payment.nextStep1')}</span>
            </li>
            <li>
              <CheckCircle size={16} />
              <span>{t('subscription.payment.nextStep2')}</span>
            </li>
            <li>
              <CheckCircle size={16} />
              <span>{t('subscription.payment.nextStep3')}</span>
            </li>
          </ul>
        </div>

        <div className={styles.actionButtons}>
          <button 
            className={styles.primaryButton}
            onClick={handleGoToDashboard}
          >
            {t('subscription.payment.backToDashboard')}
            <ArrowRight size={18} />
          </button>
          <button 
            className={styles.secondaryButton}
            onClick={handleViewSubscription}
          >
            {t('subscription.payment.viewSubscription')}
          </button>
        </div>

        <div className={styles.footer}>
          <p>{t('subscription.payment.notificationEmail')}</p>
        </div>
      </div>

      {/* Image Modal */}
      {showImage && proofUrl && (
        <div className={styles.imageModal} onClick={handleCloseImage}>
          <div className={styles.imageModalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={handleCloseImage}>
              ×
            </button>
            <img 
              src={getImageUrl(proofUrl)} 
              alt="Payment Proof"
              className={styles.proofImage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPendingPage;